import clsx from "clsx"
import {motion} from "framer-motion"
import {type RefObject, memo, useEffect, useRef, useState} from "react"

import styles from "./HeroCommonGraphic.module.scss"

const SHAPE_DIR = "/decorative-graphics/hero/common/graphic-shapes"

const MOVE_DURATION = 0.6
const HOLD_DURATION = 1
const STEP_COUNT = 3

const STEP_TRANSITION = {
	duration: MOVE_DURATION,
	ease: "easeInOut" as const,
}

const useGraphicStep = (target: RefObject<HTMLElement | null>) => {
	const [step, setStep] = useState(0)

	useEffect(() => {
		const element = target.current
		let current = 0
		let timer: ReturnType<typeof setTimeout> | undefined
		let cancelled = false
		let inView = true
		let paused = false
		let next: (() => void) | null = null
		let remaining = 0
		let startedAt = 0

		const arm = (fn: () => void, ms: number) => {
			next = fn
			remaining = ms
			startedAt = Date.now()
			if (!paused) {
				timer = setTimeout(fn, ms)
			}
		}

		const hold = () => {
			if (cancelled) {
				return
			}
			arm(move, HOLD_DURATION * 1000)
		}

		const move = () => {
			if (cancelled) {
				return
			}
			current = (current + 1) % STEP_COUNT
			setStep(current)
			arm(hold, MOVE_DURATION * 1000)
		}

		const pause = () => {
			if (paused || cancelled) {
				return
			}
			paused = true
			if (timer !== undefined) {
				clearTimeout(timer)
				timer = undefined
				remaining = Math.max(0, remaining - (Date.now() - startedAt))
			}
		}

		const resume = () => {
			if (cancelled || !paused || !next) {
				return
			}
			if (!inView || document.hidden) {
				return
			}
			paused = false
			startedAt = Date.now()
			timer = setTimeout(next, remaining)
		}

		const onVisibility = () => {
			if (document.hidden) {
				pause()
				return
			}
			resume()
		}

		const observer = element
			? new IntersectionObserver(([entry]) => {
					inView = entry.isIntersecting
					if (inView) {
						resume()
						return
					}
					pause()
			  })
			: null

		observer?.observe(element as Element)
		document.addEventListener("visibilitychange", onVisibility)
		move()

		return () => {
			cancelled = true
			if (timer !== undefined) {
				clearTimeout(timer)
			}
			observer?.disconnect()
			document.removeEventListener("visibilitychange", onVisibility)
		}
	}, [target])

	return step
}

type ShapeMotion = {
	left: string | string[]
	top: string | string[]
	width: string
	height: string
	rotate?: number | number[]
	src: string
	className: string
}

const percent = (value: number, total: number) =>
	`${((value / total) * 100).toFixed(3)}%`

const W = 1186
const H = 739
const isKeyed = <T,>(value: T | T[]): value is T[] => Array.isArray(value)

const shapes: ShapeMotion[] = [
	{
		src: `${SHAPE_DIR}/onion-ring.png`,
		className: styles.onionRing,
		left: percent(258, W),
		top: percent(143, H),
		width: percent(594, W),
		height: percent(596, H),
		rotate: [0, 6.201, 15.322],
	},
	{
		src: `${SHAPE_DIR}/ball3.png`,
		className: styles.ball3,
		left: [percent(416, W), percent(416, W), percent(416, W)],
		top: [percent(0, H), percent(0, H), percent(66, H)],
		width: percent(265, W),
		height: percent(265, H),
	},
	{
		src: `${SHAPE_DIR}/ball2.png`,
		className: styles.ball2,
		left: [percent(251, W), percent(280, W), percent(272, W)],
		top: [percent(207, H), percent(207, H), percent(170, H)],
		width: percent(212, W),
		height: percent(212, H),
	},
	{
		src: `${SHAPE_DIR}/ball1.png`,
		className: styles.ball1,
		left: [percent(80, W), percent(80, W), percent(80, W)],
		top: [percent(133, H), percent(110, H), percent(117, H)],
		width: percent(153, W),
		height: percent(153, H),
	},
	{
		src: `${SHAPE_DIR}/ball4.png`,
		className: styles.ball4,
		left: percent(817, W),
		top: percent(331, H),
		width: percent(369, W),
		height: percent(369, H),
	},
	{
		src: `${SHAPE_DIR}/carambola2.png`,
		className: styles.carambola2,
		left: percent(781, W),
		top: percent(87, H),
		width: percent(350, W),
		height: percent(343, H),
		rotate: [0, 2, 4.08],
	},
	{
		src: `${SHAPE_DIR}/carambola1.png`,
		className: styles.carambola1,
		left: percent(35.986, W),
		top: [percent(277.631, H), percent(277.631, H), percent(296.631, H)],
		width: percent(200, W),
		height: percent(196, H),
	},
]

const positionOf = (value: string | string[], step: number) =>
	isKeyed(value) ? value[step] : value

const GraphicShapes = ({step}: {step: number}) => (
	<>
		{shapes.map((shape) => {
			const moves =
				isKeyed(shape.left) ||
				isKeyed(shape.top) ||
				isKeyed(shape.rotate)
			const boxStyle = {
				width: shape.width,
				height: shape.height,
				...(!isKeyed(shape.left) ? {left: shape.left} : {left: 0}),
				...(!isKeyed(shape.top) ? {top: shape.top} : {top: 0}),
			}

			if (isKeyed(shape.rotate)) {
				return (
					<div
						key={shape.src}
						className={clsx(styles.shape, shape.className)}
						style={boxStyle}
					>
						<motion.img
							alt={""}
							animate={{rotate: shape.rotate[step]}}
							className={styles.spin}
							decoding={"async"}
							draggable={false}
							initial={{rotate: shape.rotate[0]}}
							originX={0.5}
							originY={0.5}
							src={shape.src}
							style={{transformOrigin: "50% 50%"}}
							transition={STEP_TRANSITION}
						/>
					</div>
				)
			}

			if (!moves) {
				return (
					<img
						key={shape.src}
						alt={""}
						className={clsx(styles.shape, shape.className)}
						decoding={"async"}
						draggable={false}
						src={shape.src}
						style={boxStyle}
					/>
				)
			}

			return (
				<motion.div
					key={shape.src}
					animate={{
						x: positionOf(shape.left, step),
						y: positionOf(shape.top, step),
					}}
					className={styles.slot}
					initial={{
						x: positionOf(shape.left, 0),
						y: positionOf(shape.top, 0),
					}}
					transition={STEP_TRANSITION}
				>
					<img
						alt={""}
						className={clsx(styles.shape, shape.className)}
						decoding={"async"}
						draggable={false}
						src={shape.src}
						style={{
							width: shape.width,
							height: shape.height,
							left: 0,
							top: 0,
							...(typeof shape.rotate === "number"
								? {rotate: `${shape.rotate}deg`}
								: {}),
						}}
					/>
				</motion.div>
			)
		})}
	</>
)

type Props = {
	colorBurn?: boolean
}

const HeroCommonGraphic = ({colorBurn = true}: Props) => {
	const frameRef = useRef<HTMLDivElement>(null)
	const step = useGraphicStep(frameRef)

	return (
		<>
			<div
				ref={frameRef}
				aria-hidden
				className={clsx(
					styles.frame,
					styles.colorBurn,
					colorBurn && styles.visible,
				)}
			>
				<GraphicShapes step={step} />
			</div>
			<div
				aria-hidden
				className={clsx(styles.frame, !colorBurn && styles.visible)}
			>
				<GraphicShapes step={step} />
			</div>
		</>
	)
}

export default memo(HeroCommonGraphic)
